import { useMemo, useState, type ComponentProps, type ReactNode } from 'react';
import { inTeam, teamFilter } from '../use-teams';
import { useLocation, useNavigate } from 'react-router-dom';
import { formatHours, PROJECT_STATUSES } from '@forecastify/shared';
import type {
  Client,
  Metafield,
  Project,
  ProjectMember,
  ProjectRole,
  Task,
  TaskPriority,
  TaskStatus,
  TaskTrackerValues,
  Worklog,
} from '@forecastify/shared';
import {
  Dialog,
  Button,
  DataList,
  DropdownMenu,
  Icon,
  Select,
  Tabs,
  type DataListColumn,
  type DataListRow,
  type MenuItem,
  type SortState,
} from '@forecastify/ui';
import { useT } from '../../i18n';
import { sortInMemory } from '../sort-in-memory';
import { useRowPages } from '../use-row-pages';
import { useAvatars } from '../use-avatar';
import { usePageHeader } from '../page-header';
import { Breadcrumb, CopyMenu } from '../breadcrumb';
import { Field } from '../card';
import { listLabels } from './clients-screen';
import { ProjectTasksTab } from '../tasks/project-tasks-tab';
import type { TaskFilters } from '../tasks/tasks-screen';
import { ProjectTimeTab } from './project-time-tab';
import { ProjectContractsTab } from '../contracts/project-contracts-tab';
import { ProjectResourceTab } from './project-resource-tab';
import { ProjectCostsTab } from '../contracts/project-costs-tab';
import { ProjectInvoicesTab } from '../contracts/project-invoices-tab';
import { ProjectEffortTab } from '../contracts/effort/project-effort-tab';
import { ProjectFinanceTab } from '../contracts/finance/project-finance-tab';
import { ProjectAlertsTab } from '../alerts/project-alerts-tab';
import { EntityHistory } from '../history/entity-history';
import { RiskPill } from '../alerts/risk-pill';
import { useSidebarContext } from '../sidebar/use-sidebar';
import { ProjectStartState, projectIsUsable, UNGATED_PROJECT_TABS } from './project-start-state';
import { LookbackBar } from '../history/lookback-bar';
import { useLookback } from '../history/use-lookback';
import type { TaskDialogLineItem, TaskDraft } from '../tasks/task-dialog';
import type { ClientDraft } from './client-dialog';
import { ProjectDialog, statusKey } from './project-dialog';
import type { ProjectDraft } from './project-dialog';
import type { PickableMember } from './project-dialog';
import { DateField } from '../date-field';

/**
 * Every tab id the strip can draw — what a `#hash` may name. `finance` is further gated on
 * `canSeeFinance`, as the strip itself is.
 */
const PROJECT_TABS: ReadonlySet<string> = new Set([
  'overview',
  'tasks',
  'time',
  'contracts',
  'finance',
  'costs',
  'invoices',
  'resource',
  'people',
  'alerts',
  /* ux-pass-2 · the project's own history (U-9), after Alerts. */
  'history',
]);

/**
 * 22 Oct 2026 · the product owner: a reader without the project's money sees neither its contracts
 * nor its external costs, let alone Pénzügy and Számlák — `project.finance`, as `canSeeFinance`.
 */
const MONEY_TABS: ReadonlySet<string> = new Set(['contracts', 'finance', 'costs', 'invoices']);
/** 22 Oct 2026 · the resource plan, the alerts and the history — for whoever may manage it. */
const MANAGER_TABS: ReadonlySet<string> = new Set(['resource', 'alerts', 'history']);

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ The project landing page — `markup.html` 464–660.                          │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ **ALL FOUR OF THE MOCKUP'S TABS, finally.** `pTabs` is `Dashboard · Feladatok · Idő ·
 * Emberek`. C7 shipped two of them and recorded why; **Feladatok arrived with C8** and **Idő
 * arrives with C11**, along with the two dashboard figures that count worklogs. Nothing on this
 * page is a placeholder any more, and the note that used to explain the gap is gone with it.
 *
 * ⚠️ **The tab strip still renders only what works** — the rule survives even with nothing left to
 * hide. Four tabs with one disabled would be worse than three: a disabled control is a promise
 * about the near future, and the reader cannot tell "not built" from "not permitted".
 */
export function ProjectScreen({
  project,
  canSeeFinance = true,
  clients,
  metafields,
  members,
  candidates,
  canManage,
  tasks,
  statuses,
  priorities,
  canCreateTask,
  canEditTask,
  taskFilters,
  onTaskFiltersChange,
  taskSort,
  onTaskSortChange,
  taskMetafields,
  taskTrackerValues,
  onMoveTaskStatus,
  onRescheduleTask,
  onTaskGridEdit,
  onExportTasks,
  onImportTasks,
  taskPage,
  worklogs,
  logSearch,
  logMemberId,
  logPage,
  logSort,
  onLogSortChange,
  onLogSearch,
  onLogMember,
  teams,
  memberTeams,
  logTeamId,
  onLogTeam,
  logDateRange,
  logTaskId,
  logHoursRange,
  logTaskChoices,
  onLogDateRange,
  onLogTask,
  onLogHoursRange,
  onCreateTask,
  taskLineItems,
  onOpenTask,
  onUpdateProject,
  onCreateClient,
  clientMetafields,
  onBack,
  onAddMember,
  projectRoles,
  onUpdateMember,
  onRemoveMember,
  serverError,
  loading,
}: {
  /** Until the first answer: skeleton rows and no figures. */
  readonly loading?: boolean;
  readonly project: Project;
  /** For the edit dialog's client picker. */
  readonly clients: readonly Client[];
  /** The workspace's `project` metafields, for the edit dialog below. */
  readonly metafields?: readonly Metafield[];
  readonly members: readonly ProjectMember[];
  /** Everybody in the workspace who could be added. */
  readonly candidates: readonly PickableMember[];
  readonly canManage: boolean;
  /**
   * R-58 · `project.finance` reaches THIS project — at `workspace`, or at `own` and the reader is
   * one of its members. False, and the *Pénzügy* tab is not drawn at all.
   */
  readonly canSeeFinance?: boolean;
  /** C8. The project's own tasks, with the two vocabularies needed to render and create one. */
  readonly tasks: readonly Task[];
  readonly statuses: readonly TaskStatus[];
  readonly priorities: readonly TaskPriority[];
  readonly canCreateTask: boolean;
  /**
   * ⚠️ **Item 30 · the four task views need what the global screen needs.** All of these already
   * exist on `/tasks`; the project route supplies the same, narrowed to this project.
   */
  readonly canEditTask: boolean;
  readonly taskFilters: TaskFilters;
  readonly onTaskFiltersChange: (filters: TaskFilters) => void;
  /** The workspace's task metafields: columns and filters on the Feladatok tab. */
  readonly taskMetafields: readonly Metafield[];
  /** J-1 · the tracker values this project's synced tasks carry, for the tab's three filters. */
  readonly taskTrackerValues?: TaskTrackerValues;
  /** The Feladatok list's header sort, sent to the server like `/tasks` sends it. */
  readonly taskSort: readonly SortState[];
  readonly onTaskSortChange: (next: readonly SortState[]) => void;
  readonly onMoveTaskStatus: (task: Task, statusId: string) => void;
  readonly onRescheduleTask: (task: Task, startsOn: string, dueOn: string) => void;
  readonly onTaskGridEdit: (task: Task, column: string, value: string) => void;
  readonly onExportTasks: () => void;
  readonly onImportTasks: () => void;
  /** ⚠️ The Feladatok tab's pages. Absent while a caller still loads every task at once. */
  readonly taskPage?: ComponentProps<typeof ProjectTasksTab>['page'];
  /** C11. The project's worklogs — one page of them — and the aggregate its chart draws. */
  readonly worklogs: readonly Worklog[];
  /** C12. The Idő tab's search, filter and paging, all server-side. */
  readonly logSearch: string;
  readonly logMemberId: string;
  /** 30 Oct 2026 · the Idő tab's server pages and order — `project-time-tab.tsx`. */
  readonly logPage?: ComponentProps<typeof ProjectTimeTab>['page'];
  readonly logSort: readonly SortState[];
  readonly onLogSortChange: (next: readonly SortState[]) => void;
  readonly onLogSearch: (value: string) => void;
  readonly onLogMember: (value: string) => void;
  /** R-60 · the Idő tab's team filter. Optional, so an embedded copy draws none. */
  readonly teams?: readonly { readonly id: string; readonly name: string }[];
  readonly logTeamId?: string;
  /** R-60 · who is in which team — the People tab's filter, on the client. */
  readonly memberTeams?: ReadonlyMap<string, readonly string[]>;
  readonly onLogTeam?: (value: string) => void;
  /** Round 5 item 13 — `project-time-tab.tsx` argues the shapes. */
  readonly logDateRange: string;
  readonly logTaskId: string;
  readonly logHoursRange: string;
  /** 30 Oct 2026 · the time tab's task filter options — every task on the project. */
  readonly logTaskChoices: readonly { id: string; key: string; title: string }[];
  readonly onLogDateRange: (value: string) => void;
  readonly onLogTask: (value: string) => void;
  readonly onLogHoursRange: (value: string) => void;
  /** ⚠️ **Answers whether the task was created**, so the dialog can stay open on a refusal. */
  readonly onCreateTask: (draft: TaskDraft) => Promise<boolean>;
  /** ⚠️ C25 · the project's line items, for the new-task dialog's picker. */
  readonly taskLineItems: readonly TaskDialogLineItem[];
  readonly onOpenTask: (task: Task) => void;
  /**
   * ⚠⚠️ **SAVING AN EDIT, WHERE `onEdit` USED TO MEAN "GO SOMEWHERE ELSE".** The old prop
   * navigated to `/projects` and opened nothing — which is why editing a project looked missing.
   * The dialog is on this screen now, so the route gets a draft to save rather than a request to
   * change screens.
   */
  /** Resolves to whether it landed; the edit dialog closes on `true` only. */
  readonly onUpdateProject: (draft: ProjectDraft) => Promise<boolean>;
  /**
   * 26 Oct 2026 · the edit dialog's *Új ügyfél* — absent for somebody who may not create a client.
   * Resolves to the new client's id, or `null` when the create was refused.
   */
  readonly onCreateClient?: (draft: ClientDraft) => Promise<string | null>;
  readonly clientMetafields?: readonly Metafield[];
  readonly onBack: () => void;
  /** C31 · absent `projectRoleId` means "take their default". */
  readonly onAddMember: (
    memberId: string,
    validFrom: string,
    projectRoleId?: string,
  ) => Promise<boolean>;
  /** C31 · the workspace's project roles, for the People tab's picker. */
  readonly projectRoles: readonly ProjectRole[];
  readonly onUpdateMember: (
    membership: ProjectMember,
    validFrom: string,
    validUntil: string | null,
  ) => void;
  readonly onRemoveMember: (membership: ProjectMember) => void;
  readonly serverError: string | null;
}) {
  const t = useT();

  /**
   * ⚠️ **The plan's answer, read where the sidebar already read it.** `/auth/me`'s view carries the
   * resolved flags, so this costs nothing; asking the server again per project would.
   */
  /* P2-32 · contracts are on every plan now (D1): the tabs that hang off them always show. */

  /**
   * ⚠️⚠️ **THE TAB LIVES IN THE ADDRESS'S HASH — `#overview`, `#tasks`, … — 14 Oct 2026.** The
   * product owner asked for a link that opens a project ON a tab, and the hash is the one part of
   * the address that names a place inside a screen without being a route: `routes.tsx` and the
   * reachability gate see `/projects/:id` either way.
   *
   * ⚠️ **Derived on every render, never written back.** An unknown hash — or `#finance` for a
   * reader `project.finance` does not reach — reads as the Dashboard WITHOUT rewriting the address,
   * so a permission set that arrives a moment later still honours the link it was opened with.
   *
   * ⚠️⚠️ **A TAB CLICK REPLACES THE ENTRY, IT DOES NOT PUSH ONE.** Pushing would make Back — and
   * every back arrow, which steps history (`useGoBack`) — walk through each tab somebody glanced at
   * before leaving the project. Replacing still means the entry REMEMBERS the tab, which is the half
   * that matters: a contract opened from Szerződések and closed with its arrow lands back on
   * Szerződések, because that is the address the history entry now holds.
   *
   * ⚠️ The tab ids are unchanged, so the buttons' `…-tab-<id>` ids the tour clicks still match.
   */
  const location = useLocation();
  const navigate = useNavigate();
  const requested = location.hash.replace(/^#/u, '');
  const tab =
    /* 22 Oct 2026 · the Számlák tab is money through and through, so it answers to the same gate. */
    PROJECT_TABS.has(requested) &&
    (!MONEY_TABS.has(requested) || canSeeFinance) &&
    (!MANAGER_TABS.has(requested) || canManage)
      ? requested
      : 'overview';
  const setTab = (next: string): void => {
    void navigate(
      { pathname: location.pathname, search: location.search, hash: `#${next}` },
      { replace: true },
    );
  };
  /* The shared sidebar state — null outside the chrome, and the star is then absent. */
  const sidebar = useSidebarContext();
  const pinned = sidebar?.isProjectPinned(project.id) ?? false;
  /* P2-32 · set by the gate's *Új szerződés*, so the Szerződések tab opens with its dialog. Any
     other tab change clears it, or clicking the tab later would reopen a dialog nobody asked for. */
  const [contractCreate, setContractCreate] = useState(false);
  const changeTab = (next: string): void => {
    setContractCreate(false);
    setTab(next);
  };
  /**
   * C62.1 · ⚠⚠ **THE CUT LIVES ON THE SCREEN, NOT INSIDE THE BAR, BECAUSE TWO TABS SHARE IT.**
   * Dashboard and Pénzügy both travel now, and somebody who scrubs to March on one and switches
   * to the other must still be in March — a cut held inside each bar would reset on every tab
   * change and read as the feature not working.
   *
   * ⚠️ **It is never sent with a write.** Every mutation on this screen goes through its own
   * handler and none of them reads this value; P5-2's *"one computation pass"* is a server
   * property, and this is the client half of keeping it true.
   */
  /**
   * C62.4 · ⚠️⚠️ **THE CUT LIVES HERE, NOT IN THE BAR, BECAUSE ITS CONTROLS ARE NOT ALL ON
   * THE BAR.** The mockup starts the time machine from the DASHBOARD's kebab and toggles the delta
   * there too; the bar only exists once a cut is set. A bar that owned the state could not be
   * opened from a menu outside itself.
   */
  const lookback = useLookback(project.id);
  const asOf = lookback.at;
  /* ⚠️ Lifted out of `People` so the HEADER can open the dialog the tab owns — the mockup's CTA is
     in the header and the form it opens is still the tab's. */
  const [addPersonOpen, setAddPersonOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  /**
   * ⚠️⚠️ **THE HEADER PILL IS A DROPDOWN IN THE MOCKUP, AND OURS WAS A LABEL.** `markup.html` 190
   * wraps it in a `DropdownMenu` whose items are the five statuses with a check on the current one
   * — *"Projekt státusza"*. Changing a project's status was otherwise a five-field dialog away.
   *
   * ⚠️ **It goes through the same `onUpdateProject` the dialog uses**, carrying every other field
   * unchanged, because the update endpoint takes a whole project and a `version`. A status-only
   * PATCH would be a second write path for one column, and the two would drift.
   */
  const onStatus = (status: Project['status']): void => {
    void onUpdateProject({
      keyPrefix: project.keyPrefix,
      name: project.name,
      clientId: project.clientId ?? '',
      internal: project.clientId === null,
      status,
      pmMemberId: project.pmMemberId ?? '',
      code: project.code ?? '',
      startsOn: project.startsOn ?? '',
      endsOn: project.endsOn ?? '',
      color: project.color,
      /* ⚠️ Carried through unchanged: a status flip from the header must not clear the project's
       * custom fields, and an absent `metafields` on the wire means "leave them alone". */
      metafields: project.metafields,
    });
  };

  /**
   * ⚠️⚠️ **THE PROJECT'S IDENTITY IS THE PAGE HEADER, NOT A CARD UNDER IT.** `markup.html`'s
   * `<sc-if value="{{ isProject }}">` puts all of it in the 48px bar: a back button, the name at
   * `text-[13.5px] font-medium`, the client behind a `building` icon at 12.5px, and a status **pill**
   * that is itself a dropdown. Ours rendered a second 60px-tall bar below the chrome's own — two
   * headers stacked, which is what the screenshot shows and why the screen reads as a card rather
   * than as a page.
   *
   * ⚠️ **The kebab is where `Projekt szerkesztése` lives**, per `main.jsx`'s `pKebabMenu` — and that
   * is the fix for editing being "missing": the Edit button used to `navigate('/projects')`, landing
   * somebody on the list with no dialog open and nothing to say why. The dialog now opens here, on
   * the project, which is where the mockup opens it.
   */
  usePageHeader(
    () => (
      <>
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={onBack}
          className="-ml-1.5"
          aria-label={t('projects.action.back')}
        >
          <Icon name="chevron-left" size={16} />
        </Button>

        {/**
         * ⚠️⚠️ **NO TRAIL — ITEM 9.** It read `Kezdőlap › Projektek › Nexus mag-rendszer (NEX)`, and
         * the first two segments were furniture: the sidebar is on screen with *Projektek*
         * highlighted, and the back arrow to the left of this is the same journey in one target.
         * *"A projekt headerben ami van most breadcrumb, azt le kell venni."*
         *
         * ⚠️ **The leaf keeps its own component** rather than becoming a bare `<span>`: it carries
         * the `aria-current="page"` that tells a screen reader this is the page it is on, and the
         * truncation that stops a long project name pushing the status pill off the row.
         */}
        <Breadcrumb
          crumbs={[{ label: t('projects.crumb', { name: project.name, key: project.keyPrefix }) }]}
          trailing={
            /**
             * ⚠️⚠️ **THREE THINGS ARE WORTH COPYING AND THEY ARE NOT INTERCHANGEABLE — ITEM 11.**
             * The KEY (`NEX`) is what somebody types into a task key; the IDENTIFIER
             * (`proj_a8ed9fc8`) is what an integration or a ticket quotes; the LINK is what goes
             * into a message. One button had to pick one, and picked the key.
             */
            <CopyMenu
              label={t('projects.copy.label')}
              items={[
                { label: t('projects.copy.key'), value: project.keyPrefix },
                { label: t('projects.copy.identifier'), value: project.identifier },
                { label: t('projects.copy.link'), value: window.location.href },
              ]}
            />
          }
        />

        {/* ⚠️ 17 Oct 2026 · off a phone only: there the name, its key and the copy are what fit,
            and the client and the status pill were what pushed the name to nothing. */}
        <span className="text-muted-foreground hidden items-center gap-1.5 text-[12.5px] desktop:flex">
          <Icon name="building" size={13} />
          {project.clientName ?? t('projects.internal')}
        </span>
        <div className="hidden desktop:contents">
          <ProjectStatusPill
            status={project.status}
            label={t(statusKey(project.status))}
            menuLabel={t('project.status.menu')}
            items={
              canManage
                ? PROJECT_STATUSES.map((value) => ({
                    label: t(statusKey(value)),
                    checked: value === project.status,
                    onSelect: () => {
                      if (value === project.status) return;
                      onStatus(value);
                    },
                  }))
                : null
            }
          />
        </div>

        <div className="flex-1" />

        {/**
         * C60 · ⚠⚠ **THE RISK PILL, IN THE HEADER BESIDE THE KEBAB** — which is exactly where
         * `phase5/03-project-alerts.png` draws its amber `• 60`. It belongs here rather than on the
         * Alertek tab because the question it answers is *can I trust what this page is showing
         * me*, and every tab needs that answer, not the one tab that already lists the reasons.
         *
         * ⚠️ **Nothing at all where the project has never been scanned.** In a column an em-dash
         * keeps the rows aligned; in a header it would be an ornament saying *"we have not
         * checked"* on every project between two chore ticks.
         */}
        {/* 22 Oct 2026 · the score is the alerts summed — for whoever may manage the project. */}
        {canManage ? <RiskPill t={t} score={project.riskScore} /> : null}

        {/**
         * ⚠️ **13 Oct 2026 · pin from the page itself**, beside the risk pill — the project list's
         * row menu was the only way, which meant leaving the project to pin it. The same state as
         * that menu item (`useSidebarContext`), so the two cannot disagree, and it needs nothing
         * but `project.view`: it arranges the reader's own sidebar.
         */}
        {sidebar === null ? null : (
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={pinned ? t('projects.action.unpin') : t('projects.action.pin')}
            title={pinned ? t('projects.action.unpin') : t('projects.action.pin')}
            aria-pressed={pinned}
            onClick={() => {
              sidebar.setProjectPinned(project.id, !pinned);
            }}
          >
            <Icon
              name="star"
              size={15}
              className={
                pinned ? 'fill-current text-[var(--status-warn)]' : 'text-muted-foreground'
              }
            />
          </Button>
        )}

        {canManage ? (
          <DropdownMenu
            items={[
              {
                label: t('common.edit'),
                icon: 'pencil',
                onSelect: () => {
                  setEditOpen(true);
                },
              },
              /* IP-4 · archiving is how a project is retired — it leaves the list and the sidebar
                 and waits under *Archivált projektek*; there is no delete. The status pill does
                 the same, and this is where people look for it. */
              {
                label:
                  project.status === 'archived'
                    ? t('projects.action.unarchive')
                    : t('projects.action.archiveProject'),
                icon: 'inbox',
                onSelect: () => {
                  onStatus(project.status === 'archived' ? 'active' : 'archived');
                },
              },
              /* P2-32 · ⚠️ **No *Sync now* here any more** (L7). A project names no tracker; a
                 line item does, and its own page carries the button. */
            ]}
            width={230}
            align="end"
            label={t('projects.action.menu')}
          >
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label={t('projects.action.menu')}
              /* F-2 · the tour's "editing the project" step points here, and only finds it for
                 somebody this menu is drawn for. */
              data-tour="project-actions"
            >
              <Icon name="ellipsis" size={16} />
            </Button>
          </DropdownMenu>
        ) : null}
      </>
    ),
    /* ⚠️ **VALUES ONLY** — `onBack` and `t` are deliberately absent. `onBack` is an inline arrow
     * at the call site, so including it re-ran this effect on every render and froze the app; the
     * builder reaches both through the ref. See `usePageHeader`. */
    [
      project.name,
      project.keyPrefix,
      project.clientName,
      project.status,
      canManage,
      /* ⚠️ The header's CTA is the active tab's, so it has to rebuild when the tab moves. */
      tab,
      /* ⚠️ The star. Left out, the pin lands and the header keeps drawing the old state. */
      pinned,
      sidebar === null,
    ],
  );

  return (
    <div data-screen="project" className="flex min-h-0 flex-1 flex-col">
      <ProjectDialog
        open={editOpen}
        editing={project}
        clients={clients}
        metafields={metafields}
        members={candidates}
        {...(onCreateClient === undefined ? {} : { onCreateClient })}
        {...(clientMetafields === undefined ? {} : { clientMetafields })}
        serverError={serverError}
        onClose={() => {
          setEditOpen(false);
        }}
        onSubmit={(draft) => {
          /* ⚠️ 21 Oct 2026 · it closed before the answer arrived, so a refusal took the form with
             it and left *"not available right now"* behind on a closed dialog. */
          void onUpdateProject(draft).then((landed) => {
            if (landed) setEditOpen(false);
          });
        }}
      />

      <div className="flex items-center gap-2 border-b px-4">
        <Tabs
          variant="underline"
          /* A bare tablist has no accessible name; `Tabs` requires one for that reason. */
          label={t('project.tab.overview')}
          value={tab}
          onValueChange={changeTab}
          className="flex-1 border-0"
          items={[
            { value: 'overview', label: t('project.tab.overview'), icon: 'home' },
            {
              value: 'tasks',
              label: t('project.tab.tasks'),
              icon: 'list-checks',
              /**
               * ⚠️⚠️ **THE PROJECT'S OWN COUNT, NOT THE PAGE'S.** `tasks` is one page — fifty rows
               * since the lists were paginated — so this read *50* on a project with eight hundred,
               * and so did the progress figure beside it. The projects LIST has always shown the
               * true ratio because the project row carries `taskCount` and `doneTaskCount`; the
               * detail screen was counting what it happened to be holding.
               */
              count: project.taskCount,
            },
            { value: 'time', label: t('project.tab.time'), icon: 'clock' },
            /* ⚠️ **Only when the plan has contracts** — `pTabs` filters on `flags().contracts` in
               the mockup, and the rule this screen already states applies: the strip renders only
               what works, because a disabled tab cannot say whether it is unbuilt or unlicensed. */
            /* ⚠️ **No count on this tab, and the mockup draws one.** The count is the project's
               contracts, which only the tab itself fetches — putting a number here would mean a
               second workspace-wide read on every project page, for a badge. It arrives with
               whatever gives this screen its contracts as props. */
            ...(canSeeFinance
              ? [{ value: 'contracts', label: t('title.contracts'), icon: 'file-text' as const }]
              : []),
            /**
             * C56 · ⚠️ **After the contracts, because a cost hangs off one of their line items — the
             * mockup's own `pTabs` order, and it reads as a sequence: what we agreed, what it cost
             * us elsewhere, who is planned to do it.
             *
             * ⚠️ Gated on the same flag as the contracts tab: an external cost attaches to a line
             * item, so a plan without contracts has nothing for it to point at.
             */
            /**
             * C58 · ⚠️ **Right after the Dashboard, which is the mockup's own `pTabs` order** — the
             * finance view is the second thing somebody opens a project for, and burying it behind
             * the tasks would make the money look like an afterthought.
             */
            /* R-58 · absent, not disabled, for a reader `project.finance` does not reach here. */
            ...(canSeeFinance
              ? [
                  {
                    value: 'finance',
                    label: t('project.tab.finance'),
                    icon: 'chart-column' as const,
                  },
                ]
              : []),
            ...(canSeeFinance
              ? [{ value: 'costs', label: t('extcost'), icon: 'credit-card' as const }]
              : []),
            /**
             * C57 · ⚠️ **After the costs and before the resource grid — the mockup's own `pTabs`
             * order**, which reads as a sequence: what we agreed, what it cost us elsewhere, what
             * we billed for it, who is planned to do it.
             *
             * ⚠️ The same flag again: an invoice is billed against a line item or a period, so a
             * plan without contracts has nothing for it to point at.
             */
            ...(canSeeFinance
              ? [{ value: 'invoices', label: t('invoice'), icon: 'file-text' as const }]
              : []),
            /**
             * C54 · ⚠️ **Between the contracts and the people, which is the mockup's own `pTabs`
             * order** — what we agreed to, who is planned to do it, who is on the team. No count:
             * it would be the project's allocations, which only the tab itself fetches, and a badge
             * is not worth a second workspace-wide read on every project page.
             */
            ...(canManage
              ? [
                  {
                    value: 'resource',
                    label: t('project.tab.resource'),
                    icon: 'layout-grid' as const,
                  },
                ]
              : []),
            {
              value: 'people',
              label: t('project.tab.people'),
              icon: 'users',
              ...(loading === true
                ? {}
                : { count: members.filter((member) => member.active).length }),
            },

            /**
             * C59 · ⚠⚠ **LAST, WHICH IS THE MOCKUP'S OWN ORDER AND ALSO THE RIGHT ONE.** Every
             * other tab is a thing the project IS; this one is a statement about whether the rest
             * can be believed. It reads as the footnote to the strip rather than as a tenth kind of
             * content, which is what it is.
             *
             * ⚠⚠ **NO COUNT, AND THE MOCKUP DRAWS ONE.** `alertTabCount` renders the open count
             * with a warning triangle — from a scan the prototype ran for free over an in-memory
             * object. Ours is a database read plus a reconcile per project, so a badge here would
             * put that on EVERY project page load whether or not anybody opens the tab. It arrives
             * with C60, which needs one grouped query for the whole projects list anyway and can
             * hand this screen its score without a second read.
             */
            /* 22 Oct 2026 · the alerts and the history, only to whoever may manage the project —
               the server refuses both to anybody else (`project.service`, `audit-log.service`). */
            ...(canManage
              ? [
                  {
                    value: 'alerts',
                    label: t('project.tab.alerts'),
                    icon: 'triangle-alert' as const,
                    /**
                     * ⚠️⚠️ **THE COUNT ARRIVES ON THE PROJECT ROW, WHICH IS WHY IT EXISTS NOW AND NOT
                     * IN C59.** That chunk left the badge out with a reason — the number is the
                     * project's open findings, and fetching it here would have been a workspace-wide
                     * read on every project page load for one figure. C60 put `riskScore` on the wire
                     * with a correlated subquery; this is the same shape, in the same query, for free.
                     *
                     * ⚠️ **Open and unexcepted**, the same predicate the score counts. A badge that
                     * included waived findings would say `8` over a green 100.
                     */
                    count: project.openAlertCount,
                  },
                  /**
                   * ux-pass-2 · ⚠️ **The project's history, not its tasks' — U-9.** The project, its
                   * members, allocations, costs, invoices and the contracts attached to it; a task's
                   * changes stay on the task. The same list as every other record's History.
                   */
                  { value: 'history', label: t('history.title'), icon: 'clock' as const },
                ]
              : []),
          ]}
        />
      </div>

      {/**
       * P2-32.3/.4 · ⚠️⚠️ **THE GATE, AT THE TAB SWITCH AND NOWHERE ELSE.** A project with no line
       * item cannot hold a task (`0164`), so every tab but the three that fix it shows the start
       * state instead of its content. One check here rather than one per tab, so a tab added later
       * is gated without anybody remembering to.
       */}
      {!projectIsUsable(project) && !UNGATED_PROJECT_TABS.has(tab) ? (
        <ProjectStartState
          project={project}
          onNewContract={() => {
            setTab('contracts');
            setContractCreate(true);
          }}
          onPickContract={() => {
            changeTab('contracts');
          }}
        />
      ) : tab === 'alerts' && canManage ? (
        <ProjectAlertsTab project={project} onGoToTab={changeTab} />
      ) : tab === 'history' && canManage ? (
        <div className="flex min-h-0 flex-1 flex-col">
          <EntityHistory type="project" id={project.id} showEntity />
        </div>
      ) : tab === 'finance' && canSeeFinance ? (
        /* C62.1 · the Pénzügy tab travels too — the same bar, the same cut, the same server pass. */
        <div className="flex min-h-0 flex-1 flex-col">
          <LookbackBar lookback={lookback} />
          <ProjectFinanceTab project={project} asOf={asOf} lookback={lookback} />
        </div>
      ) : tab === 'costs' && canSeeFinance ? (
        <ProjectCostsTab project={project} />
      ) : tab === 'invoices' && canSeeFinance ? (
        <ProjectInvoicesTab project={project} />
      ) : tab === 'resource' && canManage ? (
        <ProjectResourceTab project={project} />
      ) : tab === 'contracts' && canSeeFinance ? (
        <ProjectContractsTab project={project} startWithNew={contractCreate} />
      ) : tab === 'overview' ? (
        /**
         * ⚠️⚠️ **THE PHASE 1 OVERVIEW IS GONE FROM HERE, AND THAT IS THE POINT OF C65.** Phase 4
         * REPLACES this tab: progress rings, a participant list and an hours chart become earned
         * value in HOURS — the frame, the daily burn, the burn-out date and the forecast finish.
         * `PHASE-4.md`'s inventory had headings only for ADDITIONS, which is why a rebuild fell
         * between them and was reported months later by the product owner rather than planned.
         *
         * ⚠️ Nothing here is lost rather than moved: the participants are the `ERŐFORRÁS` panel of
         * the new screen, the hours chart is its `TERV / TÉNY / ELŐREJELZÉS`, and progress is the
         * `KÉSZÜLTSÉG` tile — each now measured against a frame instead of against itself.
         */
        /* C62.4 · ⚠⚠ **THE SCRUBBER SITS ABOVE THE DASHBOARD, NOT AROUND IT**, and renders
           nothing at all until a cut is set — the way in is the tab's own kebab. */
        <div className="flex min-h-0 flex-1 flex-col">
          <LookbackBar lookback={lookback} />
          <ProjectEffortTab project={project} asOf={asOf} lookback={lookback} />
        </div>
      ) : tab === 'time' ? (
        <ProjectTimeTab
          entries={worklogs}
          taskChoices={logTaskChoices}
          members={members}
          trackedHours={project.trackedHours}
          page={logPage}
          sort={logSort}
          onSortChange={onLogSortChange}
          search={logSearch}
          memberId={logMemberId}
          /* ⚠️ Round 5 item 13 — three more, all narrowing on the server. */
          dateRange={logDateRange}
          taskId={logTaskId}
          hoursRange={logHoursRange}
          serverError={serverError}
          onSearch={onLogSearch}
          onMember={onLogMember}
          {...(onLogTeam === undefined
            ? {}
            : { teams: teams ?? [], teamId: logTeamId ?? '', onTeam: onLogTeam })}
          onDateRange={onLogDateRange}
          onTask={onLogTask}
          onHoursRange={onLogHoursRange}
          loading={loading}
        />
      ) : tab === 'tasks' ? (
        <ProjectTasksTab
          loading={loading}
          {...(teams === undefined ? {} : { teams })}
          tasks={tasks}
          page={taskPage}
          /* ⚠️ Item 30 · what the four views need. The route supplies the same handlers the global
             tasks screen has, narrowed to this project. */
          filters={taskFilters}
          onFiltersChange={onTaskFiltersChange}
          sort={taskSort}
          onSortChange={onTaskSortChange}
          metafields={taskMetafields}
          {...(taskTrackerValues === undefined ? {} : { trackerValues: taskTrackerValues })}
          canEdit={canEditTask}
          onMoveStatus={onMoveTaskStatus}
          onReschedule={onRescheduleTask}
          onGridEdit={onTaskGridEdit}
          onExport={onExportTasks}
          onImport={onImportTasks}
          statuses={statuses}
          priorities={priorities}
          assignees={members
            .filter((member) => member.active)
            .map((member) => ({ id: member.memberId, name: member.displayName }))}
          canCreate={canCreateTask}
          onCreate={onCreateTask}
          lineItems={taskLineItems}
          projectId={project.id}
          onOpen={onOpenTask}
          serverError={serverError}
        />
      ) : (
        <People
          members={members}
          {...(teams === undefined ? {} : { teams })}
          {...(memberTeams === undefined ? {} : { memberTeams })}
          trackedHours={project.trackedHours}
          candidates={candidates}
          canManage={canManage}
          projectRoles={projectRoles}
          onAdd={onAddMember}
          onUpdate={onUpdateMember}
          onRemove={onRemoveMember}
          serverError={serverError}
          /* ⚠️ Both the dialog and the button that opens it are the tab's since item 25 — the
             header carries no tab-specific action any more. */
          addOpen={addPersonOpen}
          onAddOpenChange={setAddPersonOpen}
          loading={loading}
        />
      )}
    </div>
  );
}

/**
 * The Emberek tab — `markup.html` 657.
 *
 * ⚠️ **Membership is an authorization grant, and the tab says so.** Adding somebody here gives them
 * the right to log time against this project's tasks (P-1); it is not a display list. The lede
 * states that, because a control whose consequence is invisible gets used carelessly.
 */
function People({
  members,
  trackedHours,
  candidates,
  canManage,
  onAdd,
  projectRoles,
  onUpdate,
  onRemove,
  serverError,
  addOpen,
  onAddOpenChange,
  loading,
  teams = [],
  memberTeams = new Map(),
}: {
  /** Until the first answer: skeleton rows and no figures. */
  readonly loading?: boolean | undefined;
  readonly members: readonly ProjectMember[];
  /** R-60 · the team filter, on the client: the teams, and who is in which. */
  readonly teams?: readonly { readonly id: string; readonly name: string }[];
  readonly memberTeams?: ReadonlyMap<string, readonly string[]>;
  /** The project's hours, summed in SQL — the `Részarány` column's denominator. */
  readonly trackedHours: string;
  readonly candidates: readonly PickableMember[];
  readonly canManage: boolean;
  /** C31 · `projectRoleId` absent means "take their default" — see the picker. */
  readonly onAdd: (memberId: string, validFrom: string, projectRoleId?: string) => Promise<boolean>;
  /** C31 · the workspace's project roles. Empty hides the picker rather than showing an empty one. */
  readonly projectRoles: readonly ProjectRole[];
  readonly onUpdate: (
    membership: ProjectMember,
    validFrom: string,
    validUntil: string | null,
  ) => void;
  readonly onRemove: (membership: ProjectMember) => void;
  readonly serverError: string | null;
  /** ⚠️ Item 26's dialog, driven from the HEADER's CTA — see `usePageHeader` above. */
  readonly addOpen: boolean;
  readonly onAddOpenChange: (open: boolean) => void;
}) {
  const t = useT();
  /* 26 Oct 2026 · an add in flight — the dialog's buttons wait for it. */
  const [adding, setAdding] = useState(false);
  /**
   * Hours and open tasks per member, and the project's total.
   *
   * ⚠️⚠️ **THE SERVER'S, 30 Oct 2026.** These were summed over the worklogs and tasks the screen
   * had loaded — one page of each — so on any project older than a few weeks the *Óra* column
   * described the newest fifty entries. `ProjectMember` carries both figures now, counted in SQL.
   */
  const hoursBy = useMemo(
    () => new Map(members.map((member) => [member.memberId, Number(member.trackedHours)])),
    [members],
  );
  const trackedTotal = Number(trackedHours);
  const openBy = useMemo(
    () => new Map(members.map((member) => [member.memberId, member.openTasks])),
    [members],
  );
  const [query, setQuery] = useState('');
  const [teamId, setTeamId] = useState('');
  const [sort, setSort] = useState<readonly SortState[]>([]);
  const [pick, setPick] = useState('');
  const [role, setRole] = useState('');
  const [from, setFrom] = useState('');

  /* Already on the project — including a closed window, since re-adding edits that window. */
  const onProject = new Set(members.map((member) => member.memberId));
  const addable = candidates.filter((candidate) => !onProject.has(candidate.id));

  /* ⚠️ One request per distinct picture for the whole list — see `use-avatar.ts`. */
  const avatars = useAvatars(members.map((member) => member.avatarUrl));

  const rows: DataListRow[] = useMemo(() => {
    const needle = query.trim().toLowerCase();

    return sortInMemory(
      members.filter(
        (member) =>
          (needle === '' ||
            `${member.displayName} ${member.email}`.toLowerCase().includes(needle)) &&
          inTeam(memberTeams, member.memberId, teamId),
      ),
      sort,
      {
        name: (one) => one.displayName,
        email: (one) => one.email,
        role: (one) => one.projectRoleName ?? '',
        open: (one) => openBy.get(one.memberId) ?? 0,
        share: (one) => hoursBy.get(one.memberId) ?? 0,
        hours: (one) => hoursBy.get(one.memberId) ?? 0,
      },
    ).map((member) => ({
      id: member.id,
      cells: {
        name: {
          avatar: {
            initials: initialsOf(member.displayName),
            ...(member.avatarSeed === null ? {} : { seed: member.avatarSeed }),
            title: member.displayName,
            src: avatars.get(member.avatarUrl ?? ''),
          },
          /* U-10 · ⚠️ somebody U-10 KEPT rather than allows: on the project from before, or
             their time logging was switched off since. Marked, never removed. */
          ...(member.tracksTime
            ? {}
            : {
                badge: {
                  label: t('project.people.notTracking'),
                  variant: 'outline' as const,
                  title: t('project.people.notTrackingHint'),
                },
              }),
          /* ⚠️ *" · PM"* appended, as the mockup does — but as a badge rather than glued to the
           * name, so it survives sorting and does not need a Hungarian suffix. */
          text: member.isPm
            ? t('project.people.pmTag', { name: member.displayName })
            : member.displayName,
          bold: true,
          /**
           * ⚠️⚠️ **THE MEMBERSHIP WINDOW MOVED HERE FROM THREE COLUMNS OF ITS OWN.** The mockup's
           * table is six columns — name, e-mail, role, open, share, hours — and ours had eight,
           * three of them dates and a badge that are blank or identical on nearly every row. The
           * window is P-1 information and is NOT dropped: a membership that has ended, or has not
           * started, says so under the name, which is where somebody reading a row would look.
           * An ordinary current member says nothing extra, as in the mockup.
           */
          sub: member.active
            ? undefined
            : t('project.people.window', {
                from: member.validFrom,
                until: member.validUntil ?? '–',
              }),
        },
        email: { text: member.email, muted: true },
        /**
         * C31 · ⚠️ **The PROJECT ROLE, with PM still shown when there is none.** The column held
         * only *PM* before, which is a different fact — the project manager is a field on the
         * project, the role is what somebody does on it, and a Tech Lead who is also the PM is
         * both. Showing the role and falling back to the PM badge keeps the one piece of
         * information the column used to carry.
         */
        role:
          member.projectRoleName !== null
            ? {
                dot: member.projectRoleColor ?? undefined,
                text: member.projectRoleName,
              }
            : member.isPm
              ? { badge: { label: t('projects.column.pm'), variant: 'secondary' as const } }
              : { text: '–', muted: true },
        /**
         * ⚠️ **The mockup's *Nyitott*: how many of this project's tasks are on them and not
         * closed.** It answers *is this person actually carrying anything*, which neither the
         * hours nor the share can — somebody with no open work and plenty of logged hours has
         * finished, and the two rows look identical without this column.
         */
        open: {
          text: String(openBy.get(member.memberId) ?? 0),
          mono: true,
          muted: (openBy.get(member.memberId) ?? 0) === 0,
        },
        hours: { text: formatHours(hoursBy.get(member.memberId) ?? 0), mono: true },
        /**
         * ⚠️ **An en-dash when the project has NO tracked hours, not `0%`.** A share of nothing
         * is undefined — and six rows of `0%` on a project nobody has logged against reads as
         * "everybody did nothing" rather than "there is nothing to divide". The mockup guards the
         * same way (`trackedH ? … : '–'`).
         */
        share: {
          text:
            trackedTotal === 0
              ? '–'
              : `${String(Math.round(((hoursBy.get(member.memberId) ?? 0) / trackedTotal) * 100))}%`,
          mono: true,
          muted: true,
        },
      },
    }));
  }, [members, query, sort, t, hoursBy, trackedTotal, avatars, openBy, memberTeams, teamId]);

  /* ⚠️ The settled pager, over what the search and filters left — `use-row-pages.ts`. */
  const paged = useRowPages(rows, JSON.stringify([query, sort]));

  /* ⚠️ The mockup's six, in its order. `from` / `until` / `state` are gone as columns — the name
     cell carries the window on the rows where it is not the ordinary one. */
  const columns: DataListColumn[] = [
    { key: 'name', label: t('project.people.column.name'), sortKey: 'name' },
    { key: 'email', label: t('project.people.column.email'), sortKey: 'email' },
    {
      key: 'role',
      label: t('project.people.column.projectRole'),
      width: 150,
      sortKey: 'role',
    },
    {
      key: 'open',
      label: t('project.people.column.open'),
      width: 90,
      align: 'right',
      sortKey: 'open',
    },
    /* ⚠️ Share and hours order by the SAME number: a share is hours over one total, so sorting
       either sorts both. The two keys exist so a click on either header works. */
    {
      key: 'share',
      label: t('project.people.column.share'),
      width: 110,
      align: 'right',
      sortKey: 'share',
    },
    {
      key: 'hours',
      label: t('time.column.hours'),
      width: 90,
      align: 'right',
      sortKey: 'hours',
    },
  ];

  return (
    /**
     * ┌───────────────────────────────────────────────────────────────────────────┐
     * │ ⚠️⚠️ EDGE TO EDGE, LIKE EVERY OTHER TAB — 15 Sep.                         │
     * └───────────────────────────────────────────────────────────────────────────┘
     *
     * This was `gap-3 p-5`, so the table floated in a twenty-pixel margin while the Szerződések tab
     * beside it ran to both edges, and the `gap` put a band of empty card above the toolbar. Two
     * tabs of the same shape, laid out two ways, one click apart. `project-contracts-tab.tsx` is
     * the shape: a bare flex column, and the list owns its own padding.
     *
     * ⚠️ **The padding left with three things that used to need it** — a lede paragraph, an
     * add-person form and a button in a row of their own, all removed by items 10, 25 and 26. The
     * margin outlived its contents, which is the usual way a layout ends up with one.
     */
    <div className="flex min-h-0 flex-1 flex-col">
      <Dialog
        open={canManage && addOpen}
        onOpenChange={(next) => {
          if (!next) onAddOpenChange(false);
        }}
        title={t('project.people.add')}
        closeLabel={t('common.close')}
        width={520}
      >
        <div className="flex flex-col gap-3">
          <Field label={t('project.people.add')}>
            <Select
              value={pick}
              searchable={addable.length > 10}
              searchPlaceholder={t('project.people.add')}
              onChange={(event) => {
                setPick(event.target.value);
              }}
              options={[
                { value: '', label: t('common.choose') },
                /**
                 * ⚠️ U-10 · **somebody who does not log time is LISTED AND DISABLED, with the
                 * reason in the label** — `Select`'s own rule for an unpickable choice: one that
                 * quietly vanished would leave *"where is Béla?"* unanswered. The server refuses
                 * them too (`member_not_tracking`).
                 */
                ...addable.map((candidate) =>
                  candidate.tracksTime === false
                    ? {
                        value: candidate.id,
                        label: t('project.people.notTrackingOption', { name: candidate.name }),
                        disabled: true,
                      }
                    : { value: candidate.id, label: candidate.name },
                ),
              ]}
            />
          </Field>

          {/* C31 · ⚠️ **Empty means "take their default"** — the request omits the field entirely,
              and the server reads `members.default_project_role_id`. Sending `null` would mean *no
              role*, which is a different answer and the reason the two are separate on the wire. */}
          {projectRoles.length === 0 ? null : (
            <Field label={t('project.people.column.role')}>
              <Select
                value={role}
                onChange={(event) => {
                  setRole(event.target.value);
                }}
                options={[
                  { value: '', label: t('project.people.roleDefault') },
                  ...projectRoles.map((one) => ({ value: one.id, label: one.name })),
                ]}
              />
            </Field>
          )}

          {/* ⚠️ Round 5 item 30: *"— üresen hagyva a mai nap"* removed. */}
          <Field label={t('project.people.column.from')}>
            <DateField label={t('project.people.column.from')} value={from} onChange={setFrom} />
          </Field>

          {serverError === null ? null : (
            <p className="text-destructive text-[12px]" role="alert">
              {serverError}
            </p>
          )}

          <div className="flex items-center gap-2">
            <div className="flex-1" />
            <Button
              variant="outline"
              disabled={adding}
              onClick={() => {
                onAddOpenChange(false);
              }}
            >
              {t('common.cancel')}
            </Button>
            <Button
              disabled={pick === '' || adding}
              onClick={() => {
                /* 26 Oct 2026 · ⚠️ closed once it LANDS, no longer optimistically — a refusal
                   rendered `serverError` on the tab underneath a closed dialog. It stays here,
                   with the person still picked. */
                setAdding(true);
                void onAdd(pick, from, role === '' ? undefined : role).then((landed) => {
                  setAdding(false);
                  if (!landed) return;
                  setPick('');
                  setFrom('');
                  setRole('');
                  onAddOpenChange(false);
                });
              }}
            >
              {t('project.people.addAction')}
            </Button>
          </div>
        </div>
      </Dialog>

      <div className="min-h-0 flex-1">
        <DataList
          columnsKey="project-people"
          card={{ title: 'name', context: 'email', defaults: { details: ['role', 'open'] } }}
          isLoading={loading === true}
          columns={columns}
          rows={paged.rows}
          page={paged.page}
          sort={sort}
          onSortChange={setSort}
          search={query}
          onSearchChange={setQuery}
          filters={teamFilter(t, teams, teamId, setTeamId)}
          /**
           * ┌───────────────────────────────────────────────────────────────────────────┐
           * │ ⚠️⚠️ *EMBER HOZZÁADÁSA* IS A TOOLBAR ACTION — item 25, and this is its third │
           * │ position.                                                                  │
           * └───────────────────────────────────────────────────────────────────────────┘
           *
           * It began in a row of its own above the table, moved into the project header as a purple
           * CTA because the Phase 3 mockup draws it there, then lost the fill when the tester
           * objected. 15 Sep settles it: *"one line, like it is on contracts"* — every list in the
           * product now carries its create in the toolbar beside the search, and a tab is a list.
           *
           * ⚠️ **Which also deletes the `tab === 'people'` condition in the header.** A header CTA
           * that changed with the active tab had to know which tab was active; a toolbar action
           * belongs to the toolbar it is in and cannot be offered on the wrong tab.
           */
          actions={
            canManage
              ? [
                  {
                    label: t('project.people.addAction'),
                    icon: 'plus' as const,
                    /* ⚠️ The tab's main action: the right edge, in the brand fill. */
                    primary: true,
                    onSelect: () => {
                      onAddOpenChange(true);
                    },
                  },
                ]
              : []
          }
          rowMenu={
            canManage
              ? (id) => {
                  const member = members.find((candidate) => candidate.id === id);
                  if (member === undefined) return [];
                  return [
                    /**
                     * ⚠️ **Two distinct acts, and P-1 is why they are not one control.** *"They
                     * left in March"* closes the window and keeps the history; *"they should never
                     * have been added"* removes the row. A remove that silently became a
                     * window-close would make the second unexpressible.
                     */
                    {
                      label: member.active ? t('project.people.close') : t('project.people.reopen'),
                      icon: 'calendar',
                      onSelect: () => {
                        onUpdate(member, member.validFrom, member.active ? todayWire() : null);
                      },
                    },
                    {
                      label: t('project.people.remove'),
                      icon: 'trash',
                      destructive: true,
                      onSelect: () => {
                        onRemove(member);
                      },
                    },
                  ];
                }
              : undefined
          }
          empty={{
            icon: 'users',
            title: t('project.people.empty.title'),
            body: t('project.people.empty.body'),
          }}
          labels={listLabels(t)}
        />
      </div>
    </div>
  );
}

/**
 * ⚠️ **Today as a wire date, built from the LOCAL calendar rather than `toISOString()`.**
 * `toISOString()` is UTC, so at 01:00 in Budapest it names yesterday — and a membership that ends
 * "today" would end a day early for every European evening. `dates.ts` has the driver rows.
 */
function todayWire(): string {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${String(now.getFullYear())}-${month}-${day}`;
}

/** Two letters, from the first and last word. The mockup's `ini()`. */
function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return (first + last).toUpperCase();
}

/**
 * The mockup's status pill: a 22px rounded-full outline with a 6px dot.
 *
 * ⚠⚠️ **A PILL, NOT A `Badge`, AND THE DIFFERENCE IS THE MOCKUP'S OWN.** `markup.html` draws
 * `h-[22px] … rounded-full border px-2 text-[11px] font-medium` with a `size-1.5 rounded-full` dot,
 * tinted from the status colour. `Badge` is a filled rectangle and reads as a label rather than as a
 * state somebody can change.
 *
 * ⚠️ **The colour is derived from the status, not stored**, so a workspace cannot end up with a
 * pill nobody chose. `--status-*` are the design system's own semantic hues.
 *
 * ⚠️ **Colour is never the only carrier** (`design-system/readme.md`): the label is beside the dot,
 * so the state is legible without it.
 */
const STATUS_TONE: Readonly<Record<string, string>> = {
  active: 'var(--status-ok)',
  draft: 'var(--status-idle)',
  on_hold: 'var(--status-warn)',
  completed: 'var(--status-info)',
  archived: 'var(--status-idle)',
};

function ProjectStatusPill({
  status,
  label,
  menuLabel,
  items,
}: {
  readonly status: string;
  readonly label: string;
  readonly menuLabel: string;
  /** `null` when the reader cannot change it — then the pill is a `span`, not a dead button. */
  readonly items: MenuItem[] | null;
}): ReactNode {
  const tone = STATUS_TONE[status] ?? 'var(--status-idle)';
  const body = (
    <>
      <span className="size-1.5 rounded-full" style={{ background: tone }} aria-hidden />
      {label}
      {items === null ? null : <Icon name="chevron-down" size={11} className="opacity-70" />}
    </>
  );
  const className =
    /* ⚠️ 21 Oct 2026 · 32px, not the mockup's 22: every control in a header row is one height
       (`[data-control-row]`, the product owner). Still the pill, still the dot. */
    'flex h-8 shrink-0 items-center gap-1.5 rounded-full border px-3 text-[12px] font-medium';

  if (items === null) {
    return (
      <span className={className} style={{ borderColor: tone, color: tone }}>
        {body}
      </span>
    );
  }

  return (
    <DropdownMenu
      items={[{ type: 'label', label: menuLabel }, ...items]}
      width={200}
      align="start"
      label={menuLabel}
    >
      <button
        type="button"
        className={`${className} transition-colors`}
        style={{ borderColor: tone, color: tone }}
      >
        {body}
      </button>
    </DropdownMenu>
  );
}
